import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../lib/api";
import type { CommentsListResponse, Comment } from "@family/shared";

interface PostCommentsProps {
  familySlug: string;
  postId: string;
  commentCount: number;
  currentUserId?: string;
  isAdmin?: boolean;
  /** Start with the comments panel open (used in side-by-side layouts where
   *  the comments are shown alongside the photo). */
  defaultExpanded?: boolean;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-GB", {
    month: "short",
    day: "numeric",
  });
}

function AuthorAvatar({ name, avatarUrl }: { name: string | null; avatarUrl?: string | null }) {
  const initials = (name ?? "?").slice(0, 2).toUpperCase();

  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt=""
        style={{
          width: 32, height: 32, borderRadius: "50%",
          objectFit: "cover", flexShrink: 0,
        }}
      />
    );
  }

  return (
    <div style={{
      width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
      background: "linear-gradient(135deg, var(--c-accent), var(--c-accent-hover))",
      color: "var(--c-accent-contrast)", fontWeight: 700, fontSize: "0.7rem",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      {initials}
    </div>
  );
}

export function PostComments({ familySlug, postId, commentCount, currentUserId, isAdmin, defaultExpanded = false }: PostCommentsProps) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [body, setBody] = useState("");

  const commentsQuery = useQuery<CommentsListResponse>({
    queryKey: ["comments", familySlug, postId],
    queryFn: () =>
      apiFetch<CommentsListResponse>(
        `/families/${familySlug}/posts/${postId}/comments`,
      ),
    enabled: expanded,
  });

  const addComment = useMutation({
    mutationFn: (text: string) =>
      apiFetch(`/families/${familySlug}/posts/${postId}/comments`, {
        method: "POST",
        body: JSON.stringify({ body: text }),
      }),
    onSuccess: () => {
      setBody("");
      void queryClient.invalidateQueries({
        queryKey: ["comments", familySlug, postId],
      });
      void queryClient.invalidateQueries({ queryKey: ["posts", familySlug] });
    },
  });

  const deleteComment = useMutation({
    mutationFn: (commentId: string) =>
      apiFetch(`/families/${familySlug}/posts/${postId}/comments/${commentId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["comments", familySlug, postId],
      });
      void queryClient.invalidateQueries({ queryKey: ["posts", familySlug] });
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!body.trim()) return;
    addComment.mutate(body.trim());
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          padding: "6px 12px",
          minHeight: 34,
          background: "transparent",
          color: "var(--c-text-subtle)",
          border: "1px solid var(--c-border)",
          borderRadius: "var(--r-pill, 999px)",
          cursor: "pointer",
          fontSize: "0.9rem",
        }}
      >
        <span style={{ fontSize: "1rem" }}>💬</span>
        {commentCount > 0 && <span>{commentCount}</span>}
      </button>

      {expanded && (
        <div style={{ marginTop: "var(--s-3)" }}>
          {commentsQuery.isLoading && (
            <p className="text-muted" style={{ fontSize: "0.85rem" }}>Loading comments…</p>
          )}

          {commentsQuery.data && commentsQuery.data.comments.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: "var(--s-3)" }}>
              {commentsQuery.data.comments.map((c) => (
                <div key={c.id} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <AuthorAvatar name={c.author.displayName} avatarUrl={c.author.avatarUrl} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                      <span style={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        {c.author.displayName ?? "Unknown"}
                      </span>
                      <span className="text-subtle" style={{ fontSize: "0.75rem" }}>
                        {timeAgo(c.createdAt)}
                      </span>
                    </div>
                    <p style={{
                      margin: "2px 0 0", fontSize: "0.9rem",
                      lineHeight: 1.5, whiteSpace: "pre-wrap",
                    }}>
                      {c.body}
                    </p>
                    {(c.author.id === currentUserId || isAdmin) && (
                      <button
                        type="button"
                        onClick={() => deleteComment.mutate(c.id)}
                        disabled={deleteComment.isPending}
                        style={{
                          background: "none", border: "none", cursor: "pointer",
                          color: "var(--c-text-subtle)", fontSize: "0.75rem",
                          padding: "2px 0", marginTop: 2, minHeight: 0,
                        }}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {commentsQuery.data && commentsQuery.data.comments.length === 0 && (
            <p className="text-muted" style={{ fontSize: "0.85rem", marginBottom: "var(--s-3)" }}>
              No comments yet. Be the first!
            </p>
          )}

          <form onSubmit={handleSubmit} style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="text"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write a comment…"
              disabled={addComment.isPending}
              maxLength={5000}
              style={{
                flex: 1, padding: "8px 12px", fontSize: "0.9rem",
                border: "1px solid var(--c-border-strong)", borderRadius: 8,
                background: "var(--c-bg)", color: "var(--c-text)",
                minWidth: 0,
              }}
            />
            <button
              type="submit"
              disabled={addComment.isPending || !body.trim()}
              style={{
                padding: "8px 16px", fontSize: "0.85rem",
                minHeight: 36, borderRadius: 8, flexShrink: 0,
              }}
            >
              {addComment.isPending ? "…" : "Post"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
